import React from 'react';
import type { ExecutionStep } from '../services/api/agent/types';

interface ToolExecutionPanelProps {
  steps: ExecutionStep[];
  isVisible?: boolean;
}

const ToolExecutionPanel: React.FC<ToolExecutionPanelProps> = ({ steps, isVisible = true }) => {
  if (!isVisible || steps.length === 0) {
    return null;
  }

  const containerStyle: React.CSSProperties = {
    padding: '12px 16px',
    backgroundColor: 'rgba(51, 170, 64, 0.1)',
    borderBottom: '1px solid rgba(51, 170, 64, 0.3)',
    fontSize: '13px'
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
    color: '#33AA40',
    fontWeight: 500
  };

  const stepListStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  };

  const getStatusIcon = (status: ExecutionStep['status']) => {
    switch (status) {
      case 'completed':
        return (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#33AA40" strokeWidth="2">
            <polyline points="20,6 9,17 4,12" />
          </svg>
        );
      case 'failed':
        return (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#E74C3C" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        );
      case 'executing':
        return (
          <div style={{
            width: '14px',
            height: '14px',
            border: '2px solid rgba(51, 170, 64, 0.3)',
            borderTopColor: '#33AA40',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
        );
      case 'pending':
      default:
        return (
          <div style={{
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            marginLeft: '2px',
            marginRight: '2px'
          }} />
        );
    }
  };

  const getStepStyle = (status: ExecutionStep['status']): React.CSSProperties => {
    return {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: status === 'failed' ? '#E74C3C' : status === 'completed' ? '#33AA40' : 'rgba(255, 255, 255, 0.8)',
      opacity: status === 'pending' ? 0.6 : 1
    };
  };

  const formatToolName = (name: string): string => {
    return name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  return (
    <div style={containerStyle}>
      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={headerStyle}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
        Agent Actions
      </div>
      <div style={stepListStyle}>
        {steps.map((step) => (
          <div key={step.id} style={getStepStyle(step.status)}>
            {getStatusIcon(step.status)}
            <span>{step.description || formatToolName(step.tool)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ToolExecutionPanel;
